<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <div class="row">
    <div class="col">
      <card>
        <!--        Title-->
        <div class="row mb-3">
          <div class="col">
            <tab-rail
              :tabs="categories"
              :value="activeTabIndex"
              @input="switchTab"
            >
              <template #actions>
                <el-dropdown>
                  <base-button
                    link
                    type="primary"
                    size="sm"
                  > <i style="font-size: large" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a class="dropdown-item" @click="deleteAccountPrompt($event)" href="#">
                        <i class="tim-icons text-danger icon-trash-simple mr-2"></i> Delete account
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </template>
            </tab-rail>
          </div>

        </div>
<!--        Plans-->
        <div v-if="activeTabIndex === 0">
          <div class="row">
            <div class="col">
              <h4>Billing period: {{getMonthAndYear(statement.billing_cycle_start)}}</h4>
            </div>
          </div>
          <!-- Cost summary and cost breakdown, laid out like a cloud billing console -->
          <div class="row mb-4" v-if="isMinimumModel(statement)">
            <div class="col-lg-4 col-md-5 mb-3 mb-md-0">
              <collapsible-section title="Cost summary" icon="tim-icons icon-coins" :collapsible="false" class="h-100 mb-0"
                                   description="Cycle to date is the greater of your usage so far and the monthly minimum. The forecast scales usage so far across the full cycle.">
                <div class="mb-3">
                  <small class="text-white d-block">Cycle to date</small>
                  <h4 class="mb-0">{{ getAmount(statement.total) }}</h4>
                  <small v-if="samePeriodTrend" class="d-block text-white">
                    <span :class="trendClass(samePeriodTrend)">{{ trendArrow(samePeriodTrend) }} {{ samePeriodTrend.label }}</span> vs last cycle, same period
                  </small>
                  <small v-else class="d-block text-white">{{ minimumNoteText(statement) }}</small>
                </div>
                <div class="mb-3" v-if="previousSamePeriod">
                  <small class="text-white d-block">Last cycle, same period</small>
                  <h4 class="mb-0">{{ getAmount(previousSamePeriod.total) }}</h4>
                  <small class="d-block text-white">{{ samePeriodRangeLabel }}</small>
                </div>
                <div class="mb-3" v-if="projectedTotal !== null">
                  <small class="text-white d-block">Forecast for this cycle</small>
                  <h4 class="mb-0">{{ getAmount(projectedTotal) }}</h4>
                  <small v-if="forecastTrend" class="d-block text-white">
                    <span :class="trendClass(forecastTrend)">{{ trendArrow(forecastTrend) }} {{ forecastTrend.label }}</span> vs last cycle's total
                  </small>
                  <small v-else class="d-block text-white">Based on usage so far.</small>
                </div>
                <div v-if="previousStatement">
                  <small class="text-white d-block">Last cycle's total</small>
                  <h4 class="mb-0">{{ getAmount(previousStatement.total) }}</h4>
                  <small class="d-block text-white">{{ getMonthAndYear(previousStatement.billing_cycle_start) }}</small>
                </div>
              </collapsible-section>
            </div>
            <div class="col-lg-8 col-md-7" v-if="costHistoryChart">
              <collapsible-section title="Cost breakdown" icon="tim-icons icon-chart-bar-32" :collapsible="false" class="h-100 mb-0"
                                   description="Each bar is one billing cycle, split by feature. The minimum top-up is the part of a bill that usage did not cover.">
                <div class="chart-area" :style="{ height: isMobile ? '300px' : '200px' }">
                  <bar-chart style="height: 100%" rounded html-tooltip :chart-data="costHistoryChart" :extra-options="costHistoryOptions" />
                </div>
              </collapsible-section>
            </div>
          </div>
          <div class="d-none d-sm-block">
            <div class="row">
              <div class="col">
                <b-table :items="planRows" :fields="planFields">
                  <template v-slot:cell(feature)="row">
                    <p>{{row.item.name}}</p>
                    <small v-if="isEmailCampaigns(row.item)">Billed per marketing email sent. Automated emails like confirmations, reminders, and receipts are free</small>
                    <small v-if="isCommunityApi(row.item)">The first {{ communityApiFreeRequests() }} requests this billing cycle are free. Requests are counted across every key the account has had, so rolling a key does not reset the allowance</small>
                    <template v-if="row.item.is_sms">
                      <small class="d-block">{{ row.item.description }}</small>
                      <small v-if="smsNumbersLine" class="d-block">{{ smsNumbersLine }}</small>
                    </template>
                  </template>
                  <template v-slot:cell(rate)="row">
                    {{ row.item.is_sms ? invoiceItemRate(row.item) : planRate(row.item) }}
                  </template>
                  <template v-slot:cell(current_usage)="row">
                    {{ row.item.is_sms ? invoiceItemUsage(row.item, statement) : planUsage(row.item) }}
                  </template>
                  <template v-slot:cell(current_cost)="row">
                    {{getAmount(row.item.amount_due)}}
                  </template>
                </b-table>
              </div>
            </div>
          </div>
<!--          Mobile-->
          <div class="d-block d-sm-none">
            <div class="row">
              <div class="col">
                <b-table :items="planRows" :fields="['features']">
                  <!--                <template #head(feature)="data">-->
                  <!--                  <span></span>-->
                  <!--                </template>-->
                  <template v-slot:cell(features)="row">
                    <p style="font-size: large">{{row.item.name}}</p>
                    <small class="mb-2" v-if="isEmailCampaigns(row.item)">Billed per marketing email sent. Automated emails like confirmations, reminders, and receipts are free</small>
                    <small class="mb-2" v-if="isCommunityApi(row.item)">The first {{ communityApiFreeRequests() }} requests this billing cycle are free. Requests are counted across every key the account has had, so rolling a key does not reset the allowance</small>
                    <template v-if="row.item.is_sms">
                      <small class="d-block">{{ row.item.description }}</small>
                      <small v-if="smsNumbersLine" class="d-block mb-2">{{ smsNumbersLine }}</small>
                      <p>Rate: {{ invoiceItemRate(row.item) }}</p>
                      <p>Current usage: {{ invoiceItemUsage(row.item, statement) }}</p>
                    </template>
                    <template v-else>
                      <p>Rate: {{ planRate(row.item) }}</p>
                      <p>Current usage: {{ planUsage(row.item) }}</p>
                    </template>
                    <p>Current cost: {{getAmount(row.item.amount_due)}}</p>
                  </template>
                </b-table>
              </div>
            </div>
          </div>

          <!-- Greater of the monthly minimum or usage: same three lines as the pricing page estimator -->
          <div class="row" v-if="isMinimumModel(statement)">
            <div class="col-md-6 col-lg-5 ml-auto text-right">
              <small class="text-white d-block">You pay</small>
              <h2 class="mb-1">{{ getAmount(statement.total) }}</h2>
              <p class="text-white mb-1">
                Usage {{ getAmount(statement.usage_total) }}
                &nbsp;&middot;&nbsp; Monthly minimum {{ getAmount(statement.minimum_amount) }}
                <template v-if="Number(statement.sms_items_total)">&nbsp;&middot;&nbsp; SMS {{ getAmount(statement.sms_items_total) }}</template>
                <template v-if="otherChargesTotal(statement) >= 0.005">&nbsp;&middot;&nbsp; Other charges {{ getAmount(otherChargesTotal(statement)) }}</template>
              </p>
              <hr class="my-2">
              <small class="text-white d-block">{{ minimumNoteText(statement) }}</small>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col d-sm-none d-block text-right">
              <h4>Total due: {{ getAmount(statement.total) }}</h4>
            </div>
            <div class="col d-sm-block d-none">
              <h4>Total due: {{ getAmount(statement.total) }}</h4>
            </div>
          </div>
        </div>

<!--        Billing-->
        <div v-if="activeTabIndex === 1">
          <div v-if="billing && !add_payment">
            <div class="row">
              <div class="col">
                <label>Payment method</label>
                <p><span class="font-weight-600">{{ billing.card_type }}: </span>xxxx-xxxx-xxxx-{{billing.last_four}}</p>
                <p>
                  <span class="font-weight-600"></span>
                  {{billing.accountholder_first_name}} {{billing.accountholder_last_name}}
                </p>
                <p v-if="billing.email">
                  <span class="font-weight-600">Email: </span>{{ billing.email }}
                </p>
                <p v-if="billing.phone">
                  <span class="font-weight-600">Phone: </span>{{ formatPhone(billing.phone) }}
                </p>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <label>Billing address</label>
                <p>{{billing.address_line_1}}</p>
                <p>{{billing.address_line_2}}</p>
                <p>{{billing.city}}, {{billing.state}}, {{billing.zip}}</p>
                <p>{{billing.country.name}}</p>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <base-button type="primary" v-on:click="editBillingInfo()">
                  Edit billing information</base-button>
              </div>
            </div>
          </div>


          <div v-if="!billing && !add_payment">
            <div class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <h4 class="mt-3">Billing information has not been added yet.</h4>
            </div>
            <div class="row">
              <div class="col">
                <base-button type="primary" v-on:click="addBillingInfo()">
                  Add billing information</base-button>
              </div>
            </div>
          </div>

          <div v-if="add_payment">
            <div class="row">
              <div class="col">
                <h4 class="card-title">Add a new credit card</h4>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-3">
                <base-input v-model="billing.accountholder_first_name" label="Cardholder first name" placeholder="Cardholder first name"></base-input>
              </div>
              <div class="col-lg-3">
                <base-input v-model="billing.accountholder_last_name" label="Cardholder last name" placeholder="Cardholder last name"></base-input>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-3">
                <base-input
                  v-model="billing.email"
                  label="Billing email"
                  placeholder="email@example.com"
                  type="email"
                  :error="emailError"
                  @input="validateEmail(billing.email)"
                >
                </base-input>
                <span v-if="emailError" class="text-danger" style="font-size: 0.875rem">{{ emailError }}</span>
              </div>
              <div class="col-lg-3">
                <base-input
                  type="text"
                  label="Phone (Optional)"
                >
                  <phone-number-input
                    ref="phoneInput"
                    v-model="billing.phone"
                    @phone-update="handlePhoneUpdate"
                    :default-country="billing.country ? billing.country.code : 'US'"
                  />
                </base-input>
              </div>
            </div>

            <!--                  Square-->
            <label>Card details</label>
            <div class="row">
              <div class="col-lg-6">
                <form id="payment-form-square" >
                  <div id="card-container"></div>
                </form>
                <div id="payment-status-container"></div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h4 class="card-title">Billing address</h4>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-6">
                <base-input v-model="billing.address_line_1" label="Address line 1" placeholder="Address line 1"></base-input>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-6">
                <base-input v-model="billing.address_line_2" label="Address line 2" placeholder="Address line 2"></base-input>
              </div>
            </div>

            <div class="row">
              <div class="col-lg-3">
                <base-input v-model="billing.city"  :label="billing.country.city_label" :placeholder="billing.country.city_label"></base-input>
              </div>
              <div class="col-lg-3">
                <base-input v-if="billing.country.state_label === 'State'" :label="billing.country.state_label">
                  <el-select
                    v-model="billing.state"
                    class="select-primary"
                    :placeholder="billing.country.state_label"
                  >
                    <el-option
                      v-for="(s, i) in states"
                      class="select-primary"
                      :label="s.name"
                      :value="s.name"
                      :key="'S' + i"
                    ></el-option>
                  </el-select>
                </base-input>
                <base-input v-else :placeholder="billing.country.state_label" :label="billing.country.state_label" v-model="billing.state"></base-input>
              </div>
            </div>
            <div class="row">
              <div class="col-lg-2">
                <base-input v-model="billing.zip"  :label="billing.country.zip_label" :placeholder="billing.country.zip_placeholder"></base-input>
              </div>
              <div class="col-lg-4">
                <base-input label="Country">
                  <el-select
                             v-model="billing.country"
                             class="select-primary"
                             name="email_group"
                             placeholder="Country"
                             value-key="code"
                  >
                    <el-option
                      v-for="(c, i) in countries"
                      class="select-primary"
                      :label="c.name"
                      :value="{name: c.name, code: c.code, city_label: c.city_label,
                      state_label: c.state_label, zip_label: c.zip_label, zip_placeholder: c.zip_placeholder}"
                      :key="'C' + i"
                    ></el-option>
                  </el-select>
                </base-input>
              </div>
            </div>

            <div class="row mt-2">
              <div class="col">
                <base-button type="default" v-on:click="cancelAddPayment()">Cancel</base-button>
                <base-button type="primary" class="ml-2" v-on:click="createBillingInfo()">Save billing information</base-button>
              </div>
            </div>
          </div>
        </div>

  <!--        Invoice-->
        <div v-if="activeTabIndex === 2">
          <filter-bar
            :filters="filterSchema"
            :initial-value="filterValues"
            :result-count="invoiceResultCount"
            :loading="invoicesRefreshing"
            :per-page="perPage"
            :per-page-options="perPageOptions"
            saved-filters-key="billing-invoices"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          >
            <template #actions>
              <base-button v-if="hasPermissions('download_data')" icon class="btn-link text-white" v-on:click="downloadCSV()">
                <i class="tim-icons icon-cloud-download-93"></i>
              </base-button>
            </template>
          </filter-bar>
          <div v-if="mergedInvoices.length <= 0" class="font-icon-detail">
              <span style="font-size: 3em">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
            <h4 class="mt-3">No invoices exist yet.</h4>
          </div>

          <div class="d-none d-sm-block">
            <div class="row" v-if="mergedInvoices.length > 0">
              <div class="col">
                <b-table :items="mergedInvoices" :fields="[{ key: 'billing_period', label: 'Billing period / Description' }, 'invoice_id', 'payment_date', 'payment_method', 'status', 'amount', { key: 'actions', label: 'Actions', thClass: 'text-right' }]">
                  <template v-slot:cell(amount)="row">
                    <span v-if="row.item.type === 'one_off'">{{getAmount(row.item.amount_due)}}</span>
                    <span v-else>{{getAmount(row.item.actual_total - row.item.discount)}}</span>
                  </template>
                  <template v-slot:cell(invoice_id)="row">
                    <span v-if="row.item.invoice_id">#{{row.item.invoice_id}}</span>
                    <span v-else>-</span>
                  </template>
                  <template v-slot:cell(actions)="row">
                    <div class="d-flex align-items-center justify-content-end">
                      <!-- Always rendered so every row reserves the same slot and the
                           download chips line up; hidden (not clickable) when not payable. -->
                      <base-button
                        variant="outlined"
                        tone="primary"
                        class="mr-2"
                        :class="{ invisible: !(row.item.type === 'one_off' && row.item.status === 'unpaid') }"
                        @click.native="openPayDrawer(row.item)"
                      ><i class="tim-icons icon-credit-card"></i> Pay invoice</base-button>
                      <base-button
                        variant="outlined"
                        tone="neutral"
                        @click.native="downloadInvoice(row.item)"
                      ><i class="tim-icons icon-cloud-download-93"></i> Download</base-button>
                    </div>
                  </template>
                  <template v-slot:cell(payment_date)="row">
                    <span v-if="row.item.type === 'one_off' && row.item.charged_at">{{getLocalDate(row.item.charged_at)}}</span>
                    <span v-else-if="row.item.type !== 'one_off' && row.item.time_paid">{{getLocalDate(row.item.time_paid)}}</span>
                    <span v-else>-</span>
                  </template>
                  <template v-slot:cell(payment_method)="row">
                    <span v-if="row.item.type === 'one_off' && row.item.status === 'paid' && row.item.payment_last_four">Card ending in {{row.item.payment_last_four}}</span>
                    <span v-else-if="row.item.type !== 'one_off' && row.item.status === 1">Card ending in {{row.item.payment_last_four}}</span>
                    <span v-else>-</span>
                  </template>
                  <template v-slot:cell(billing_period)="row">
                    <span v-if="row.item.type === 'one_off'">{{row.item.description}}</span>
                    <span v-else>{{getMonthAndYear(row.item.billing_cycle_start)}}</span>
                  </template>
                  <template v-slot:cell(status)="row">
                    <template v-if="row.item.type === 'one_off'">
                      <badge v-if="row.item.status === 'unpaid'" type="danger">unpaid</badge>
                      <badge v-if="row.item.status === 'paid'" type="success">paid</badge>
                    </template>
                    <template v-else>
                      <badge v-if="row.item.status === 2" type="warning">payment failed</badge>
                      <badge v-if="row.item.status === 1" type="success">paid</badge>
                      <badge v-if="row.item.status === 0" type="danger">due</badge>
                    </template>
                  </template>
                </b-table>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="onPageChange"
                ></b-pagination>
              </div>
            </div>
          </div>

          <div class="d-block d-sm-none">
            <div class="row" v-if="mergedInvoices.length > 0">
              <div class="col">
                <b-table :items="mergedInvoices" :fields="['invoices']">
                  <template v-slot:cell(invoices)="row">
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="text-white" style="font-size: 16px; font-weight: 600;">
                        <span v-if="row.item.type === 'one_off'">{{row.item.description}}</span>
                        <span v-else>{{getMonthAndYear(row.item.billing_cycle_start)}}</span>
                      </span>
                      <template v-if="row.item.type === 'one_off'">
                        <badge v-if="row.item.status === 'unpaid'" type="danger">unpaid</badge>
                        <badge v-if="row.item.status === 'paid'" type="success">paid</badge>
                      </template>
                      <template v-else>
                        <badge v-if="row.item.status === 2" type="warning">payment failed</badge>
                        <badge v-if="row.item.status === 1" type="success">paid</badge>
                        <badge v-if="row.item.status === 0" type="danger">due</badge>
                      </template>
                    </div>

                    <div class="detail-row text-white" v-if="row.item.invoice_id">
                      <span>Invoice number</span>
                      <span>#{{row.item.invoice_id}}</span>
                    </div>
                    <div class="detail-row detail-row--total text-white">
                      <span>Amount</span>
                      <span v-if="row.item.type === 'one_off'">{{getAmount(row.item.amount_due)}}</span>
                      <span v-else>{{getAmount(row.item.actual_total - row.item.discount)}}</span>
                    </div>

                    <template v-if="row.item.type === 'one_off'">
                      <div class="detail-row text-white" v-if="row.item.status === 'paid' && row.item.charged_at">
                        <span>Payment date</span>
                        <span>{{getLocalDate(row.item.charged_at)}}</span>
                      </div>
                      <div class="detail-row text-white" v-if="row.item.status === 'paid' && row.item.payment_last_four">
                        <span>Payment method</span>
                        <span>Card ending in {{row.item.payment_last_four}}</span>
                      </div>
                    </template>
                    <template v-else>
                      <div class="detail-row text-white" v-if="row.item.status === 1 && row.item.time_paid">
                        <span>Payment date</span>
                        <span>{{getLocalDate(row.item.time_paid)}}</span>
                      </div>
                      <div class="detail-row text-white" v-if="row.item.status === 1 && row.item.payment_last_four">
                        <span>Payment method</span>
                        <span>Card ending in {{row.item.payment_last_four}}</span>
                      </div>
                    </template>

                    <div class="d-flex align-items-center justify-content-end mt-3">
                      <base-button
                        v-if="row.item.type === 'one_off' && row.item.status === 'unpaid'"
                        variant="outlined"
                        tone="primary"
                        class="mr-2"
                        @click.native="openPayDrawer(row.item)"
                      ><i class="tim-icons icon-credit-card"></i> Pay invoice</base-button>
                      <base-button
                        variant="outlined"
                        tone="neutral"
                        @click.native="downloadInvoice(row.item)"
                      ><i class="tim-icons icon-cloud-download-93"></i> Download</base-button>
                    </div>
                  </template>
                </b-table>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="onPageChange"
                ></b-pagination>
              </div>
            </div>
          </div>


          <vue-html2pdf
            :show-layout="false"
            :float-layout="true"
            :paginate-elements-by-height="800"
            :enable-download="false"
            filename="invoice"
            :pdf-quality="2"
            :manual-pagination="false"
            pdf-format="a4"
            pdf-orientation="portrait"
            pdf-content-width="800px"
            ref="invoicePdf"
            @beforeDownload="beforeDownload($event)"
          >
            <section slot="pdf-content">
              <div style="color: #222a42; font-family: 'Poppins', sans-serif; width: 800px; padding: 0 0 32px 0; background: #ffffff; background-image: linear-gradient(90deg, #c14fc1 0%, #7a6bc2 50%, #2b9db8 100%); background-size: 100% 6px; background-repeat: no-repeat;">
                <div style="padding: 36px 44px 0 44px;">

                  <!-- Header -->
                  <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                    <div>
                      <img height="46" src="/img/icons/android-chrome-512x512.png" style="display: block; margin-bottom: 12px;">
                      <p style="margin: 0; font-size: 13px; font-weight: 600; color: #222a42;">Off The Couch Inc.</p>
                      <p style="margin: 0; font-size: 12px; color: #6b7280;">555 Mowry Ave., Fremont CA 94536</p>
                      <p style="margin: 0; font-size: 12px; color: #6b7280;">+1 (408) 478-2341</p>
                    </div>
                    <div style="text-align: right;">
                      <p style="margin: 0; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8;">Invoice</p>
                      <p style="margin: 2px 0 10px 0; font-size: 26px; font-weight: 600; color: #222a42; line-height: 1.2;">#{{ selectedInvoice.invoice_id }}</p>
                      <span :style="invoiceStatusStyle(selectedInvoice)">{{ invoiceStatusLabel(selectedInvoice) }}</span>
                    </div>
                  </div>

                  <!-- Parties and dates -->
                  <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 34px; padding-top: 24px; border-top: 1px solid #e6e8ee;">
                    <div style="width: 48%;">
                      <p style="margin: 0 0 8px 0; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8;">Billed to</p>
                      <p v-if="selectedInvoice.billed_to_first_name" style="margin: 0; font-size: 14px; font-weight: 600; color: #222a42;">{{ selectedInvoice.billed_to_first_name }} {{ selectedInvoice.billed_to_last_name }}</p>
                      <p v-if="selectedInvoice.billed_to_email" style="margin: 0; font-size: 13px; color: #4b5563;">{{ selectedInvoice.billed_to_email }}</p>
                      <p v-if="selectedInvoice.billed_to_phone" style="margin: 0; font-size: 13px; color: #4b5563;">{{ formatPhone(null, selectedInvoice.billed_to_phone) }}</p>
                      <p v-if="selectedInvoice.address_line_1" style="margin: 0; font-size: 13px; color: #4b5563;">{{ selectedInvoice.address_line_1 }}</p>
                      <p v-if="selectedInvoice.address_line_2" style="margin: 0; font-size: 13px; color: #4b5563;">{{ selectedInvoice.address_line_2 }}</p>
                      <p v-if="cleanAddressLine(selectedInvoice.address_line_3)" style="margin: 0; font-size: 13px; color: #4b5563;">{{ cleanAddressLine(selectedInvoice.address_line_3) }}</p>
                      <p v-if="!selectedInvoice.billed_to_first_name && !selectedInvoice.billed_to_email" style="margin: 0; font-size: 13px; color: #4b5563;">-</p>
                    </div>
                    <table style="width: 46%; border-collapse: collapse; font-size: 13px;">
                      <tr v-if="selectedInvoice.billing_cycle_end">
                        <td style="padding: 0 0 6px 0; color: #6b7280;">Billing period</td>
                        <td style="padding: 0 0 6px 0; text-align: right; font-weight: 600; color: #222a42;">{{ getMonthAndYear(selectedInvoice.billing_cycle_start) }}</td>
                      </tr>
                      <tr>
                        <td style="padding: 0 0 6px 0; color: #6b7280;">Date issued</td>
                        <td style="padding: 0 0 6px 0; text-align: right; font-weight: 600; color: #222a42;">{{ getInvoiceDate(selectedInvoice.billing_cycle_start) }}</td>
                      </tr>
                      <tr>
                        <td style="padding: 0 0 6px 0; color: #6b7280;">Date due</td>
                        <td style="padding: 0 0 6px 0; text-align: right; font-weight: 600; color: #222a42;">{{ getInvoiceDate(selectedInvoice.time_due) }}</td>
                      </tr>
                      <tr v-if="selectedInvoice.time_paid">
                        <td style="padding: 0; color: #6b7280;">Date paid</td>
                        <td style="padding: 0; text-align: right; font-weight: 600; color: #222a42;">{{ getLocalDate(selectedInvoice.time_paid) }}</td>
                      </tr>
                    </table>
                  </div>

                  <!-- Line items -->
                  <table style="width: 100%; border-collapse: collapse; margin-top: 34px; font-size: 13px;">
                    <thead>
                    <tr>
                      <th style="padding: 0 0 10px 0; text-align: left; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8; border-bottom: 2px solid #2b9db8;">Feature</th>
                      <th style="padding: 0 12px 10px 0; text-align: left; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8; border-bottom: 2px solid #2b9db8;">Usage</th>
                      <th style="padding: 0 12px 10px 0; text-align: right; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8; border-bottom: 2px solid #2b9db8;">Rate</th>
                      <th style="padding: 0 0 10px 0; text-align: right; font-size: 11px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #2b9db8; border-bottom: 2px solid #2b9db8;">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(b, i) in invoiceLineItems(selectedInvoice)" :key="'ITEMS' + i">
                      <td style="padding: 11px 12px 11px 0; vertical-align: top; border-bottom: 1px solid #eef0f4; color: #222a42;">
                        <span style="font-weight: 500;">{{ b.name }}</span>
                        <br v-if="invoiceItemNote(b, selectedInvoice.items)">
                        <span v-if="invoiceItemNote(b, selectedInvoice.items)" style="font-size: 11px; color: #6b7280;">{{ invoiceItemNote(b, selectedInvoice.items) }}</span>
                        <br v-if="b.source === 'sms' && b.description">
                        <span v-if="b.source === 'sms' && b.description" style="font-size: 11px; color: #6b7280;">{{ b.description }}</span>
                      </td>
                      <td style="padding: 11px 12px 11px 0; vertical-align: top; border-bottom: 1px solid #eef0f4; color: #4b5563;">{{ invoiceItemUsage(b, selectedInvoice) }}</td>
                      <td style="padding: 11px 12px 11px 0; vertical-align: top; border-bottom: 1px solid #eef0f4; text-align: right; white-space: nowrap; color: #4b5563;">{{ invoiceItemRate(b) }}</td>
                      <td style="padding: 11px 0; vertical-align: top; border-bottom: 1px solid #eef0f4; text-align: right; white-space: nowrap; font-weight: 500;" :style="{ color: parseFloat(b.amount) > 0 ? '#222a42' : '#9ca3af' }">{{ getAmount(b.amount) }}</td>
                    </tr>
                    </tbody>
                  </table>

                  <!-- Totals -->
                  <div style="display: flex; justify-content: flex-end; margin-top: 18px;">
                    <table style="width: 46%; border-collapse: collapse; font-size: 13px;">
                      <!-- Greater of the monthly minimum or usage: same lines as the plan tab -->
                      <tr v-if="isMinimumModel(selectedInvoice)">
                        <td style="padding: 6px 0; color: #6b7280;">Usage</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(selectedInvoice.usage_total) }}</td>
                      </tr>
                      <tr v-if="isMinimumModel(selectedInvoice)">
                        <td style="padding: 6px 0; color: #6b7280;">Monthly minimum</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(selectedInvoice.minimum_amount) }}</td>
                      </tr>
                      <tr v-if="isMinimumModel(selectedInvoice) && smsItemsTotal(selectedInvoice.items) >= 0.005">
                        <td style="padding: 6px 0; color: #6b7280;">SMS</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(smsItemsTotal(selectedInvoice.items)) }}</td>
                      </tr>
                      <tr v-if="isMinimumModel(selectedInvoice) && Math.abs(invoiceOtherCharges(selectedInvoice) - smsItemsTotal(selectedInvoice.items)) >= 0.005">
                        <td style="padding: 6px 0; color: #6b7280;">Other charges</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(invoiceOtherCharges(selectedInvoice) - smsItemsTotal(selectedInvoice.items)) }}</td>
                      </tr>
                      <tr>
                        <td style="padding: 6px 0; color: #6b7280;">Total</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(selectedInvoice.actual_total) }}</td>
                      </tr>
                      <tr v-if="isMinimumModel(selectedInvoice)">
                        <td colspan="2" style="padding: 0 0 6px 0; font-size: 11px; color: #6b7280;">{{ minimumNoteText(selectedInvoice) }}</td>
                      </tr>
                      <tr v-if="selectedInvoice.discount > 0 || computed_dicount > 0">
                        <td style="padding: 6px 0; color: #c14fc1;">Discount</td>
                        <td style="padding: 6px 0; text-align: right; color: #c14fc1;">-{{ getAmount(selectedInvoice.discount) }}</td>
                      </tr>
                      <tr>
                        <td style="padding: 6px 0; color: #6b7280;">Amount paid</td>
                        <td style="padding: 6px 0; text-align: right; color: #222a42;">{{ getAmount(selectedInvoice.paid) }}</td>
                      </tr>
                      <tr>
                        <td style="padding: 12px 14px; font-weight: 700; color: #222a42; border-radius: 6px 0 0 6px;" :style="{ background: selectedInvoice.due > 0 ? 'rgba(193, 79, 193, 0.12)' : 'rgba(43, 157, 184, 0.14)' }">Amount due</td>
                        <td style="padding: 12px 14px; text-align: right; font-size: 16px; font-weight: 700; border-radius: 0 6px 6px 0;" :style="{ background: selectedInvoice.due > 0 ? 'rgba(193, 79, 193, 0.12)' : 'rgba(43, 157, 184, 0.14)', color: selectedInvoice.due > 0 ? '#a93aa9' : '#1f7f96' }">{{ getAmount(selectedInvoice.due) }}</td>
                      </tr>
                    </table>
                  </div>

                  <!-- Footer -->
                  <p style="margin: 40px 0 0 0; padding-top: 16px; border-top: 1px solid #e6e8ee; font-size: 12px; color: #6b7280;">
                    Questions about this invoice? Email us at <a href="mailto:contact@offthecouch.io" style="color: #2b9db8; text-decoration: none;">contact@offthecouch.io</a>
                  </p>
                </div>
              </div>
            </section>

          </vue-html2pdf>

          <pay-invoice-drawer
            :show="pay_drawer_open"
            :invoice="invoice_to_pay || {}"
            :amount-display="invoice_to_pay ? getAmount(parseFloat(invoice_to_pay.amount_due)) : ''"
            :square-app-id="squareAppId"
            :square-location-id="squareLocationId"
            @close="pay_drawer_open = false"
            @paid="onInvoicePaid"
          />
        </div>
      </card>
    </div>
  </div>
</div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import {isValidEmail} from "@/util/emailValidator";
import swal from "sweetalert2";
import countries from "@/assets/countries.json";
import states from "@/assets/states.json";
import {BPagination, BTable} from "bootstrap-vue";
import currencies from "@/assets/currencies.json";
import _ from "lodash";
import BaseSwitch from "@/components/BaseSwitch";
import Badge from "@/components/Badge";
import BarChart from "@/components/Charts/BarChart";
import * as chartConfigs from "@/components/Charts/config";
import {dayOfWeek, formattedDate} from "@/plugins/dateFormatter";
import {formatGroupDate, date as formatDatePart} from "@/plugins/dates";
import {Dropdown, DropdownItem, DropdownMenu} from "element-ui";
import VueHtml2pdf from "vue-html2pdf";
import moment from "moment";
import {AmplifyEventBus} from "aws-amplify-vue";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import {formatPhoneDisplay} from "@/util/phoneFormatter";
import PayInvoiceDrawer from "./PayInvoiceDrawer.vue";
import TabRail from "@/components/TabRail.vue";

const EMAIL_CAMPAIGNS_FEATURE_ID = 30
const COMMUNITY_API_FEATURE_ID = 31
// Free allowance per billing cycle: 10,000 for cycles before October 2026, 3,000 from then on
const COMMUNITY_API_FREE_REQUESTS_UNTIL_SEPT_2026 = 10000
const COMMUNITY_API_FREE_REQUESTS = 3000
// Checklists (3), surveys (4) and waivers (6) are free while a bundling feature is enabled.
// Email campaigns (30) and Community API (31) never bundle, matching the daily cost cron
const BUNDLED_FEATURE_IDS = [3, 4, 6]
const NON_BUNDLING_FEATURE_IDS = [3, 4, 6, EMAIL_CAMPAIGNS_FEATURE_ID, COMMUNITY_API_FEATURE_ID]
// Customer copy says event, the features row stores the unit as game
const UNIT_ALIASES = { game: 'event' }


export default {
  name: "BillingPage",
  components: {
    BaseSwitch,
    BarChart,
    BTable,
    BPagination,
    Badge,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    VueHtml2pdf,
    PhoneNumberInput,
    PayInvoiceDrawer,
    TabRail
  },
  data(){
    return {
      loading: false,
      trial_ended: true,
      trial_start: '',
      trial_end: '',
      computed_dicount: 0,
      countries: countries.countries,
      states: states.states,
      currencies: currencies.currencies,
      number_format: JSON.parse(localStorage.getItem('group')).number_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      currency_symbol: '$',
      activeTabIndex: 0,
      add_payment: false,
      perPageOptions: [5, 10, 25, 50],
      emailError: '',
      billing: {
        email: '',
        phone: '',
        phoneData: null,
        country:{
          "city_label": "City",
          "state_label": "State",
          "zip_label": "Zip",
          "zip_placeholder": "95133",
        }
      },
      square: '',
      card: '',
      squareAppId: '',
      squareLocationId: '',
      features: [],
      // SMS context for the plan tab (numbers and credits reset date), from get-billing-information
      sms: null,
      // Custom items on the open statement; the SMS rows on the plan tab come from these
      customItems: [],
      invoices: [],
      oneOffInvoices: [],
      statement: [],
      previousStatement: null,
      previousSamePeriod: null,
      costHistory: [],
      isMobile: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      // Flat filter values for the shared <filter-bar>; FilterBar resolves saved-view precedence
      filterValues: {
        invoice_id: '',
        status: '',
        period: { mode: 'days', days: '', from: '', to: '' }
      },
      didInitialInvoiceFetch: false,
      invoicesRefreshing: false,
      pay_drawer_open: false,
      invoice_to_pay: null,
      selectedInvoice: {
        billed_to_first_name: '',
        billed_to_last_name: '',
        billed_to_email: '',
        billed_to_phone: '',
        time_due: '',
        time_paid: ''
      }
    }
  },
  computed: {
    // Full-cycle estimate: usage so far scaled by days elapsed over days in the cycle, then the
    // same greater-of rule as the statement, plus custom items. Cycles are calendar months
    // (billing_cycle_start is the 1st). Null until day 4, and null when the open statement is
    // not this month's, so a single busy day never drives the figure.
    projectedTotal() {
      const s = this.statement
      if (!this.isMinimumModel(s) || !s.billing_cycle_start) return null
      const parts = s.billing_cycle_start.split('T')[0].split('-')
      const year = Number(parts[0])
      const monthIndex = Number(parts[1]) - 1
      const today = new Date()
      if (today.getFullYear() !== year || today.getMonth() !== monthIndex) return null
      const daysElapsed = today.getDate()
      if (daysElapsed < 4) return null
      const daysInCycle = new Date(year, monthIndex + 1, 0).getDate()
      const usage = parseFloat(s.usage_total) || 0
      const minimum = parseFloat(s.minimum_amount) || 0
      const customItems = parseFloat(s.custom_items_total) || 0
      const projectedUsage = usage / daysElapsed * daysInCycle
      const cents = Math.round(Number((Math.max(projectedUsage, minimum) + customItems).toFixed(6) + 'e2'))
      return cents / 100
    },
    // Change of the cycle-to-date figure against last cycle's same-period figure
    samePeriodTrend() {
      if (!this.previousSamePeriod || !this.isMinimumModel(this.statement)) return null
      return this.trend(parseFloat(this.statement.total) || 0, parseFloat(this.previousSamePeriod.total) || 0)
    },
    // Change of the forecast against last cycle's total
    forecastTrend() {
      if (this.projectedTotal === null || !this.previousStatement) return null
      return this.trend(this.projectedTotal, parseFloat(this.previousStatement.total) || 0)
    },
    samePeriodRangeLabel() {
      if (!this.previousSamePeriod) return ''
      const from = this.previousSamePeriod.from
      const to = this.previousSamePeriod.to
      // to is exclusive; show the last day that counted
      const last = new Date(to + 'T00:00:00')
      last.setDate(last.getDate() - 1)
      return this.getMonthAndYear(from).split(' ')[0] + ' ' + Number(from.slice(8, 10)) + ' to ' + last.getDate()
    },
    // Stacked bars: one dataset per feature line across the last six cycles, plus the minimum
    // top-up and other charges. Legacy statements before the greater-of model show their lines too.
    costHistoryChart() {
      if (!this.costHistory || this.costHistory.length === 0) return null
      // Phones get the last three cycles so the bars stay readable
      const history = this.isMobile ? this.costHistory.slice(-3) : this.costHistory
      const labels = history.map(h => {
        const my = this.getMonthAndYear(h.billing_cycle_start) || ''
        return this.isMobile ? my.slice(0, 3) : my.slice(0, 3) + ' ' + my.slice(-4)
      })
      // Theme hues in hue order, softened so stacked segments read as one bar. Every series
      // gets its own color: SMS keeps the orange, the two adjustments their own neutrals, and
      // a feature line past the palette takes a golden-angle hue so nothing repeats
      const smsColor = 'rgba(255,141,114,0.85)'
      // Eight hues that read apart on the dark card: magenta, blue, teal, yellow, red, violet,
      // lime, white. Orange, grey and tan are reserved for the series below. Colors are handed
      // out after empty lines are dropped, so a skipped line never leaves two neighbours alike
      const palette = ['rgba(225,78,202,0.85)', 'rgba(29,140,248,0.85)', 'rgba(0,242,195,0.85)', 'rgba(255,204,0,0.85)', 'rgba(244,67,54,0.85)', 'rgba(137,101,224,0.85)', 'rgba(164,224,75,0.85)', 'rgba(255,255,255,0.75)']
      const lineColor = i => (i < palette.length ? palette[i] : `hsla(${Math.round((30 + (i - palette.length) * 137.508) % 360)}, 70%, ${i % 2 ? 75 : 45}%, 0.85)`)
      const names = []
      for (const h of history) {
        for (const line of h.lines) {
          if (!names.includes(line.name)) names.push(line.name)
        }
      }
      const datasets = names.map(name => ({
        label: name,
        data: history.map(h => {
          const line = h.lines.find(l => l.name === name)
          return line ? (parseFloat(line.amount) || 0) : 0
        })
      })).filter(set => set.data.some(amount => Math.abs(amount) >= 0.005))
        .map((set, i) => ({ ...set, backgroundColor: lineColor(i) }))
      datasets.push({
        label: 'Minimum top-up',
        backgroundColor: 'rgba(125,125,125,0.5)',
        data: history.map(h => parseFloat(h.minimum_adjustment) || 0)
      })
      // SMS rides as system custom items; it gets its own series and leaves Other charges to the manual items
      const smsSeries = history.map(h => parseFloat(h.sms_items_total) || 0)
      if (smsSeries.some(amount => Math.abs(amount) >= 0.005)) {
        datasets.push({
          label: 'SMS',
          backgroundColor: smsColor,
          data: smsSeries
        })
      }
      const otherSeries = history.map(h => {
        const cents = Math.round(Number((parseFloat(h.custom_items_total) || 0).toFixed(6) + 'e2')) - Math.round(Number((parseFloat(h.sms_items_total) || 0).toFixed(6) + 'e2'))
        return cents / 100
      })
      if (otherSeries.some(amount => Math.abs(amount) >= 0.005)) {
        datasets.push({
          label: 'Other charges',
          backgroundColor: 'rgba(188,143,101,0.85)',
          data: otherSeries
        })
      }
      return { labels, datasets }
    },
    costHistoryOptions() {
      const base = chartConfigs.barChartOptionsSecondary
      return {
        ...base,
        legend: { display: true, position: 'bottom', labels: { fontColor: '#9e9e9e', boxWidth: 10, padding: this.isMobile ? 8 : 14, fontSize: this.isMobile ? 10 : 12 } },
        tooltips: {
          ...base.tooltips,
          mode: 'index',
          intersect: true,
          position: 'nearest',
          titleFontSize: 13,
          bodyFontSize: 12,
          // Only the lines that billed that cycle, with the cycle total as the footer
          filter: item => Math.abs(item.yLabel) >= 0.005,
          callbacks: {
            label: (item, data) => data.datasets[item.datasetIndex].label + ': ' + this.getAmount(item.yLabel),
            footer: items => 'Total ' + this.getAmount(items.reduce((sum, item) => sum + (Number(item.yLabel) || 0), 0))
          },
          footerFontColor: '#333',
          footerFontStyle: 'bold'
        },
        scales: {
          yAxes: [{ ...base.scales.yAxes[0], stacked: true, ticks: { ...base.scales.yAxes[0].ticks, padding: 10, maxTicksLimit: this.isMobile ? 4 : 8, callback: value => this.wholeAmount(value) } }],
          xAxes: [{ ...base.scales.xAxes[0], stacked: true, barPercentage: 0.9, categoryPercentage: 0.75, ticks: { ...base.scales.xAxes[0].ticks, padding: 6, maxRotation: 0 }, gridLines: { ...base.scales.xAxes[0].gridLines, display: false } }]
        }
      }
    },
    // "Off The Couch (510) 399-0036 · Credits reset 11/01/2026": which numbers the SMS rows cover
    smsNumbersLine() {
      if (!this.sms || !this.sms.numbers || !this.sms.numbers.length) return ''
      const numbers = this.sms.numbers
        .filter(n => n.subscribed_this_cycle)
        .map(n => n.group_name + (n.display_phone_number ? ' ' + n.display_phone_number : ''))
      const parts = numbers.length ? [numbers.join(', ')] : []
      if (this.sms.credits_reset_on) parts.push('Credits reset ' + this.getFormattedDate(this.sms.credits_reset_on))
      return parts.join(' · ')
    },
    planFields() {
      return [
        { key: 'feature', label: 'Feature' },
        { key: 'rate', label: 'Rate' },
        { key: 'current_usage', label: 'Current usage' },
        { key: 'current_cost', label: 'Current cost' },
      ]
    },
    // Feature lines plus the statement's stored SMS items (source 'sms'), so the plan tab shows
    // exactly what the invoice will: the daily cron's saved quantity, rate and amount
    planRows() {
      const smsRows = (this.customItems || [])
        .filter(item => item.source === 'sms')
        .map(item => ({ ...item, is_sms: true, amount_due: item.amount }))
      return this.features.concat(smsRows)
    },
    categories() {
      return [
        { name: 'Your plan', shortName: 'Plan' },
        { name: 'Billing information', shortName: 'Billing' },
        { name: 'Invoices', shortName: 'Invoices' }
      ];
    },
    filterSchema() {
      return [
        {key: 'invoice_id', type: 'text', label: 'Invoice number', placeholder: 'Invoice number', operatorLabel: 'contains', primary: true},
        {key: 'status', type: 'select', label: 'Status', primary: true, options: [
          {label: 'Paid', value: 'paid'},
          {label: 'Due', value: 'due'},
          {label: 'Payment failed', value: 'failed'},
        ]},
        {key: 'period', type: 'daterange', label: 'Billing period', primary: true},
      ]
    },
    invoiceResultCount() {
      return this.totalRows + this.oneOffInvoices.length
    },
    mergedInvoices() {
      const monthly = this.invoices.map(m => ({ ...m, type: 'monthly' }))
      if (this.currentPage !== 1) {
        return monthly
      }
      const oneOffs = this.oneOffInvoices.map(o => ({ ...o, type: 'one_off' }))
      return [...oneOffs, ...monthly].sort((a, b) => {
        const dateA = a.type === 'one_off' ? a.created_at : a.billing_cycle_start
        const dateB = b.type === 'one_off' ? b.created_at : b.billing_cycle_start
        return new Date(dateB) - new Date(dateA)
      })
    }
  },
  methods: {
    // Below the sm breakpoint the chart shows fewer cycles and a tighter legend
    updateIsMobile() {
      this.isMobile = window.innerWidth < 576
    },
    // Percent change of current against previous; null when previous is zero (nothing to compare)
    trend(current, previous) {
      if (!(previous > 0)) return null
      const pct = Math.round((current - previous) / previous * 100)
      return { direction: pct > 0 ? 'up' : (pct < 0 ? 'down' : 'flat'), label: (pct === 0 ? 'No change' : Math.abs(pct) + '%') }
    },
    trendArrow(trend) {
      if (trend.direction === 'up') return '\u2191'
      if (trend.direction === 'down') return '\u2193'
      return ''
    },
    // Lower cost is the good direction
    trendClass(trend) {
      if (trend.direction === 'up') return 'text-danger'
      if (trend.direction === 'down') return 'text-success'
      return 'text-white'
    },
    // Axis ticks: the group's currency format without a zero fraction
    wholeAmount(value) {
      return this.getAmount(Math.round(value)).replace(/([.,])00(?!\d)/, '')
    },
    // Statements priced as greater-of(minimum, usage) carry minimum_amount; earlier ones do not
    isMinimumModel(statement) {
      return !!statement && statement.minimum_amount !== null && statement.minimum_amount !== undefined
    },
    // Wording matches the pricing page estimator
    // Same wording as the invoice emails. SMS and manual items sit on top of the greater-of result
    minimumNoteText(statement) {
      const usage = parseFloat(statement.usage_total) || 0
      const minimum = parseFloat(statement.minimum_amount) || 0
      // Plan tab statements carry custom_items_total; invoices carry their items
      const extras = (parseFloat(statement.custom_items_total) || 0) !== 0 || Math.abs(this.invoiceOtherCharges(statement)) >= 0.005
      const suffix = extras ? ' plus any SMS and other charges.' : '.'
      if (usage < minimum) {
        return 'Usage is under the minimum, so the bill is a flat ' + this.getAmount(minimum) + suffix
      }
      return extras ? 'Usage covers the minimum, so you pay usage exactly' + suffix : 'Usage covers the minimum, so you pay usage exactly. Never both.'
    },
    // Invoice rows: a greater-of statement lists only the lines with usage, then custom items
    invoiceLineItems(invoice) {
      const items = (invoice && invoice.items) || []
      if (!this.isMinimumModel(invoice)) {
        return items
      }
      return items.filter(item => !item.feature_id
        || (Number(item.feature_usage) || 0) > 0 || (parseFloat(item.amount) || 0) > 0)
    },
    // System SMS items (source 'sms') shown as their own total; summed in cents
    smsItemsTotal(items) {
      const cents = (items || [])
        .filter(item => !item.feature_id && item.source === 'sms')
        .reduce((sum, item) => sum + Math.round(Number((parseFloat(item.amount) || 0).toFixed(6) + 'e2')), 0)
      return cents / 100
    },
    // Hand-entered items on the open statement: every custom item minus the SMS ones
    otherChargesTotal(statement) {
      const all = Math.round(Number((parseFloat(statement.custom_items_total) || 0).toFixed(6) + 'e2'))
      const sms = Math.round(Number((parseFloat(statement.sms_items_total) || 0).toFixed(6) + 'e2'))
      return (all - sms) / 100
    },
    smsStatusLabel(status) {
      const value = String(status || '')
      if (value === 'active') return 'Active'
      if (value === 'cancelled') return 'Cancelled'
      if (value === 'suspended') return 'Suspended'
      if (value === 'paused') return 'Paused'
      if (value.endsWith('_review')) return 'In review'
      if (value.endsWith('_rejected')) return 'Action needed'
      return 'In progress'
    },
    // Custom line items carry no feature_id
    invoiceOtherCharges(invoice) {
      return ((invoice && invoice.items) || [])
        .filter(item => !item.feature_id)
        .reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0)
    },
    planRate(feature) {
      return this.getRate(feature.price_per_unit) + ' / ' + this.pluralizeUnit(feature.unit, 1)
    },
    planUsage(feature) {
      const usage = Number(feature.feature_usage) || 0
      return usage.toLocaleString() + ' ' + this.pluralizeUnit(feature.unit, usage)
    },
    isEmailCampaigns(feature) {
      return feature.feature_id === EMAIL_CAMPAIGNS_FEATURE_ID
    },
    isCommunityApi(feature) {
      return feature.feature_id === COMMUNITY_API_FEATURE_ID
    },
    communityApiFreeRequests(cycleStartValue) {
      const source = cycleStartValue !== undefined ? cycleStartValue : (this.statement && this.statement.billing_cycle_start)
      const cycleStart = String(source || '').slice(0, 10)
      const free = cycleStart && cycleStart < '2026-10-01' ? COMMUNITY_API_FREE_REQUESTS_UNTIL_SEPT_2026 : COMMUNITY_API_FREE_REQUESTS
      return free.toLocaleString()
    },
    // Invoice line items: feature rows carry a unit and usage, custom line items and one-off invoices do not
    pluralizeUnit(unit, count) {
      const base = UNIT_ALIASES[unit] || unit || 'unit'
      return Number(count) === 1 ? base : base + 's'
    },
    // Feature lines carry feature_usage and per_unit_price; stored custom items (SMS) carry quantity and unit_price
    invoiceItemUsage(item, invoice) {
      if (!item.unit) {
        return ''
      }
      const usage = Number(item.feature_usage !== undefined && item.feature_usage !== null ? item.feature_usage : item.quantity) || 0
      let text = usage.toLocaleString() + ' ' + this.pluralizeUnit(item.unit, usage)
      if (this.isCommunityApi(item)) {
        text += ', first ' + this.communityApiFreeRequests(invoice.billing_cycle_start) + ' free'
      }
      return text
    },
    invoiceItemRate(item) {
      if (!item.unit) {
        return ''
      }
      const rate = item.per_unit_price !== undefined && item.per_unit_price !== null ? item.per_unit_price : item.unit_price
      return this.getRate(rate) + ' / ' + this.pluralizeUnit(item.unit, 1)
    },
    // Explains a line whose amount is not usage times rate: a waived bundle feature or a monthly minimum
    invoiceItemNote(item, items) {
      // Per-feature minimums and the bundle only exist on statements from before the monthly minimum
      if (this.isMinimumModel(this.selectedInvoice)) {
        return ''
      }
      const amount = parseFloat(item.amount) || 0
      const featureId = Number(item.feature_id)
      if (BUNDLED_FEATURE_IDS.includes(featureId) && amount === 0) {
        const bundleEnabled = (items || []).some(row =>
          row.feature_id && !NON_BUNDLING_FEATURE_IDS.includes(Number(row.feature_id)) && Number(row.is_enabled) === 1)
        return bundleEnabled ? 'Included with your bundle' : ''
      }
      const basePrice = parseFloat(item.base_price) || 0
      const usageCost = (Number(item.feature_usage) || 0) * (parseFloat(item.per_unit_price) || 0)
      if (basePrice > 0 && amount === basePrice && usageCost < basePrice) {
        return this.getAmount(basePrice) + ' monthly minimum applied'
      }
      return ''
    },
    invoiceStatusLabel(invoice) {
      const due = parseFloat(invoice.due) || 0
      const paid = parseFloat(invoice.paid) || 0
      if (due <= 0) {
        return 'Paid'
      }
      return paid > 0 ? 'Partially paid' : 'Unpaid'
    },
    invoiceStatusStyle(invoice) {
      const paid = (parseFloat(invoice.due) || 0) <= 0
      return {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: '700',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: paid ? '#1f7f96' : '#a93aa9',
        background: paid ? 'rgba(43, 157, 184, 0.14)' : 'rgba(193, 79, 193, 0.12)'
      }
    },
    // The address line arrives comma-joined with empty parts, drop the empties
    cleanAddressLine(line) {
      return String(line || '').split(',').map(part => part.trim()).filter(Boolean).join(', ')
    },
    // Statement dates are calendar dates stored at UTC midnight, so format the date part
    // directly instead of shifting it into the group's timezone
    getInvoiceDate(d) {
      if (!d) {
        return ''
      }
      const group = JSON.parse(localStorage.getItem('group') || '{}')
      return formatDatePart(String(d).slice(0, 10), group.date_format || 'MM/DD/YYYY')
    },
    // Charged features first by amount, then name; custom line items stay after the features
    sortInvoiceItems(featureItems, customItems) {
      const sorted = featureItems.slice().sort((a, b) => {
        const diff = (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0)
        return diff !== 0 ? diff : String(a.name).localeCompare(String(b.name))
      })
      return sorted.concat(customItems)
    },
    // Rates carry up to 4 decimals (0.003 per request), amounts stay at 2
    getRate(value) {
      const number = parseFloat(value) || 0
      let text = number.toFixed(4)
      // Strip trailing zeros only beyond two decimals: 0.1000 prints 0.10, 0.0030 prints 0.003
      while (text.endsWith('0') && text.split('.')[1].length > 2) {
        text = text.slice(0, -1)
      }
      if (this.number_format === 1) {
        text = text.replace('.', ',')
      }
      return this.currency_format === 1 ? text + ' USD' : '$' + text
    },
    formatPhone(country, phone) {
      return formatPhoneDisplay(phone, country || 'US');
    },
    handlePhoneUpdate(phoneData) {
      this.billing.phoneData = phoneData;
      this.billing.phone = phoneData.e164 || phoneData.raw;
    },
    validateEmail(email) {
      if (!email) {
        this.emailError = 'Email is required';
        return false;
      }
      if (!isValidEmail(email)) {
        this.emailError = 'Please enter a valid email address';
        return false;
      }
      this.emailError = '';
      return true;
    },
    calculateProportionalDiscount(
      first_of_the_month,
      eligibleDays,
      total_amount_for_month
    ) {
      // Parse the date with moment.js
      let monthStart = moment(first_of_the_month, 'YYYY-MM-DD');

      // Calculate the total number of days in the current month
      let totalDaysInMonth = monthStart.daysInMonth();
      console.log("totalDaysInMonth: " + totalDaysInMonth)
      console.log("eligibleDays: " + eligibleDays)
      console.log("eligibleDays / totalDaysInMonth: " + eligibleDays / totalDaysInMonth)

      // Calculate the proportional charge
      if (totalDaysInMonth > 0) {
        return (eligibleDays / totalDaysInMonth) * total_amount_for_month;
      } else {
        return 0;
      }
    },
    getTrialDaysEligibilityThisMonth(
      first_of_the_month,
      timeCreated,
      trial_start,
      trial_end
    ) {
      // Parse dates with moment.js
      let start = moment(trial_start, 'YYYY-MM-DD');
      let end = moment(trial_end, 'YYYY-MM-DD');
      let created = moment(timeCreated, 'YYYY-MM-DD');

      // Set the current month's start and end dates
      let monthStart = moment(first_of_the_month, 'YYYY-MM-DD').startOf('month');
      let nextMonthStart = moment(first_of_the_month, 'YYYY-MM-DD')
        .add(1, 'month')
        .startOf('month');

      // Check if timeCreated falls within the same month and year as trial_start
      if (monthStart.isSame(start, 'month') && monthStart.isSame(start, 'year')) {
        return end.diff(start, 'days') + 1;
      }

      // Determine eligible start and end dates within the month
      let eligibleStart = start.isAfter(monthStart) ? start : monthStart;
      let eligibleEnd = end.isBefore(nextMonthStart)
        ? end
        : nextMonthStart.clone().subtract(1, 'day');

      console.log("eligibleStart" + moment(trial_start, 'YYYY-MM-DD').format('YYYY-MM-DD'))
      // Calculate the total eligible days
      if (eligibleEnd.isSameOrAfter(eligibleStart)) {
        return eligibleEnd.diff(eligibleStart, 'days') + 1;
      } else {
        return 0;
      }
    },
    deleteAccountPrompt(event){
      event.preventDefault()

      swal({
        title: 'Are you sure?',
        text: "This will delete your account and all users will lose access. If there is anything we can help you with, please reach out to contact@offthecouch.io",
        type: 'warning',
        confirmButtonText: 'Delete',
        showCancelButton: true
      }).then((response)=> {
        if(response.value == true){
          this.deleteAccount()
        }
      })
    },
    async deleteAccount(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId

      this.axios.delete(url, config)
        .then(response => {
          this.loading = false
          Auth.signOut().then(response => {
            AmplifyEventBus.$emit('authState', 'signedOut')
            this.$router.push('/login')
          })

        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else if (err.response.status == 500) {
            swal('Error!', err.response.data, 'error')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    async beforeDownload({html2pdf, options, pdfContent}) {
      this.loading = true
      await html2pdf().set(options).from(pdfContent).toPdf().get('pdf').then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages()
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i)
          pdf.setFontSize(10)
          pdf.setTextColor(150)
          pdf.text('Page ' + i + ' of ' + totalPages, (pdf.internal.pageSize.getWidth() * 0.88), (pdf.internal.pageSize.getHeight() - 0.3))
        }
      })
        .save()
      this.loading = false
    },
    downloadInvoice(item) {
      if (item.type === 'one_off') {
        const billing = this.billing || {}
        const country = billing.country && billing.country.name ? billing.country.name : ''
        const cityStateZip = [billing.city, billing.state, billing.zip].filter(Boolean).join(', ')
        const addressLine3 = [cityStateZip, country].filter(Boolean).join(', ')
        const amountDue = parseFloat(item.amount_due) || 0
        const amountPaid = parseFloat(item.amount_paid) || 0
        this.selectedInvoice = {
          billing_cycle_start: item.created_at,
          time_due: item.due_date,
          invoice_id: item.invoice_id,
          items: [{ name: item.description, amount: amountDue }],
          actual_total: amountDue,
          discount: 0,
          paid: amountPaid,
          due: amountDue - amountPaid,
          billed_to_first_name: billing.accountholder_first_name,
          billed_to_last_name: billing.accountholder_last_name,
          billed_to_email: billing.email,
          billed_to_phone: billing.phone,
          address_line_1: billing.address_line_1,
          address_line_2: billing.address_line_2,
          address_line_3: addressLine3,
        }
      } else {
        this.selectedInvoice = item
      }
      this.$refs.invoicePdf.generatePdf()
    },
    getDayOfWeek(d) {
      return ''
    },
    getFormattedDate(d) {
      return formattedDate(this.date_format, d)
    },
    getLocalDate(d) {
      return formatGroupDate(d)
    },
    getMonthAndYear(dateString) {
      // Manually parse the date string to avoid any timezone issues
      if (!dateString) {
        return
      }
      const parts = dateString.split('T')[0].split('-');
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1; // Months are 0-indexed
      const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
      const month = monthNames[monthIndex];
      return `${month} ${year}`;
    },
    getAmount(integer) {
      if (integer == null) {
        return
      }
      if (this.number_format === 1 && this.currency_format === 0) {
        return integer.toFixed(3).endsWith('0') ?
          '$' + integer.toLocaleString(undefined, {minimumFractionDigits: 2}).replace('.', ',') :
          '$' + integer.toLocaleString(undefined, {minimumFractionDigits: 3}).replace('.', ',')
      } else if (this.number_format === 1 && this.currency_format === 1) {
        return integer.toFixed(3).endsWith('0') ?
          integer.toLocaleString(undefined, {minimumFractionDigits: 2}).replace('.', ',') + ' ' + 'USD' :
          integer.toLocaleString(undefined, {minimumFractionDigits: 3}).replace('.', ',') + ' ' + 'USD'

      } else if (this.number_format === 1 && this.currency_format === 1) {
        return integer.toFixed(3).endsWith('0') ?
          parseFloat(integer).toFixed(2) + ' ' + 'USD' :
          parseFloat(integer).toFixed(3) + ' ' + 'USD'
      }
      return integer.toFixed(3).endsWith('0') ?
        '$' + parseFloat(integer).toFixed(2) :
        '$' + parseFloat(integer).toFixed(3)
    },
    getAuthToken() {
      return new Promise(function (resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    addBillingInfo() {
      if (!this.billing) {
        this.billing = {
          accountholder_first_name: '',
          accountholder_last_name: '',
          address_line_1: '',
          address_line_2: '',
          email: '',
          phone: '',
          phoneData: null,
          zip: '',
          state: '',
          city: '',
          country: _.find(this.countries, {'name': 'United States'})
        }
      }
      this.emailError = '';
      this.add_payment = true
      this.initializeSquare()
    },
    cancelAddPayment() {
      this.add_payment = false
    },
    editBillingInfo() {
      this.add_payment = true
      this.initializeSquare()
    },
    async tokenize(paymentMethod) {
      const tokenResult = await paymentMethod.tokenize();

      if (tokenResult.status === 'OK') {
        const zip = tokenResult.details.billing.postalCode ? tokenResult.details.billing.postalCode : ''
        return {token: tokenResult.token, zip}
      } else {
        let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
        if (tokenResult.errors) {
          errorMessage += ` and errors: ${JSON.stringify(
            tokenResult.errors
          )}`;
        }
        throw new Error(errorMessage);
      }
    },
    async verifyBuyerForCard(tokenResult) {
      const verificationDetails = {
        intent: 'STORE',
        billingContact: {
          givenName: this.billing.accountholder_first_name,
          familyName: this.billing.accountholder_last_name,
          email: this.billing.email,
          phone: this.billing.phone,
          addressLines: [this.billing.address_line_1, this.billing.address_line_2].filter(Boolean),
          city: this.billing.city,
          state: this.billing.state,
          postalCode: this.billing.zip,
          countryCode: this.billing.country ? this.billing.country.code : 'US',
        }
      }
      const verificationResult = await this.square.verifyBuyer(tokenResult.token, verificationDetails)
      return verificationResult ? verificationResult.token : null
    },
    async createBillingInfo() {
      if (!this.validateEmail(this.billing.email)) {
        this.loading = false;
        return;
      }

      this.loading = true
      let token = ''
      let verificationToken = null
      try {
        token = await this.tokenize(this.card);
        verificationToken = await this.verifyBuyerForCard(token)
      } catch (error) {
        console.error('Error:', error);
        swal('Error!', 'Could not verify your card. Please try again.', 'error')
        this.loading = false
        return
      }

      if (!verificationToken) {
        swal('Verification required', 'Card verification was not completed. Please try again.', 'warning')
        this.loading = false
        return
      }

      let tok = await this.getAuthToken()
      let config = {headers: {Authorization: tok}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/billing'
      let data = {
        billing: this.billing,
        token: token,
        verificationToken: verificationToken
      }

      this.axios.post(url, data, config)
        .then(response => {
          swal('Success!', 'Billing information has been updated', 'success')
          this.add_payment = false
          this.getBillingInfo()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else if (err.response.status === 500){
            swal.fire({
              title: 'Error!',
              text: err.response.data,
              type: 'error',
              confirmButtonColor: '#fd5d93',
              confirmButtonText: 'Correct error'
            })
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })

    },
    async getBillingInfo() {
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/billing'
      this.axios.get(url, config)
        .then(response => {
          this.billing = response.data.billing
          if (this.billing) {
            this.billing.country = _.find(this.countries, {'code': this.billing.country})
          }
          this.features = response.data.features
          this.sms = response.data.sms || null
          this.customItems = response.data.custom_items || []

          this.statement = response.data.statement
          this.previousStatement = response.data.previous_statement || null
          this.previousSamePeriod = response.data.previous_same_period || null
          this.costHistory = response.data.cost_history || []
          this.squareAppId = response.data.square.squareAppId
          this.squareLocationId = response.data.square.squareLocationId
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })

    },
    hasPermissions(scope) {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      return permissions.some(element => element === scope)
    },
    // Calendar-date bounds for the billing period filter (statements are keyed by cycle start date)
    computePeriodRange() {
      const d = this.filterValues.period || {}
      const toIso = (date) => date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2)
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        const start = new Date()
        start.setDate(start.getDate() - Number(d.days))
        return { from: toIso(start), to: toIso(new Date()) }
      }
      if (d.mode === 'range' && d.from && d.to) {
        return { from: d.from, to: d.to }
      }
      return { from: '', to: '' }
    },
    invoicesUrl(count, offset) {
      const companyId = localStorage.getItem('companyId')
      const { from, to } = this.computePeriodRange()
      return API_LOCATION + 'companies/' + companyId + '/billing/invoices?count=' + count + '&offset=' + offset
        + '&invoice_id=' + encodeURIComponent(this.filterValues.invoice_id || '')
        + '&status=' + encodeURIComponent(this.filterValues.status || '')
        + '&from=' + from + '&to=' + to
    },
    onFiltersChanged(values) {
      this.filterValues = {...this.filterValues, ...values, period: {...values.period}}
      // The first change is FilterBar's saved-views resolution and drives the initial fetch
      if (!this.didInitialInvoiceFetch) {
        this.didInitialInvoiceFetch = true
      } else {
        this.currentPage = 1
      }
      this.getInvoices(this.perPage, this.getOffset())
    },
    onPerPageChange(n) {
      this.perPage = n
      this.currentPage = 1
      this.getInvoices(this.perPage, this.getOffset())
    },
    onPageChange(page) {
      this.currentPage = page
      this.getInvoices(this.perPage, this.getOffset())
    },
    async getInvoices(count, offset) {
      this.invoicesRefreshing = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      this.axios.get(this.invoicesUrl(count, offset), config)
        .then(response => {
          this.invoices = response.data.invoices
          for (let n in this.invoices) {
            this.invoices[n].items = this.sortInvoiceItems(JSON.parse(this.invoices[n].items), JSON.parse(this.invoices[n].custom_items))
          }
          this.totalRows = response.data.total_rows
          this.oneOffInvoices = response.data.one_off_invoices || []
          this.invoicesRefreshing = false
        })
        .catch(err => {
          this.invoicesRefreshing = false
          console.log(err)
          if (err.response.status == 401) {
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })

    },
    escapeCSVValue(value) {
      if (typeof value === 'string') {
        value = value.replace(/"/g, '""')
        if (value.includes(',') || value.includes('\n')) {
          value = `"${value}"`
        }
      }
      return value
    },
    invoiceStatusText(item) {
      if (item.type === 'one_off') {
        return item.status === 'paid' ? 'Paid' : 'Unpaid'
      }
      return { 0: 'Due', 1: 'Paid', 2: 'Payment failed' }[item.status] || ''
    },
    // Every statement matching the current filters, plus the filtered one-off invoices
    async downloadCSV() {
      this.loading = true
      try {
        let token = await this.getAuthToken()
        let config = {headers: {Authorization: token}}
        const pageSize = 500
        let offset = 0
        let statements = []
        let oneOffs = []
        let total = 0
        do {
          const response = await this.axios.get(this.invoicesUrl(pageSize, offset), config)
          statements = statements.concat(response.data.invoices || [])
          total = response.data.total_rows || 0
          if (offset === 0) {
            oneOffs = response.data.one_off_invoices || []
          }
          offset += pageSize
        } while (offset < total)

        const rows = oneOffs.map(o => ({ ...o, type: 'one_off' })).concat(statements.map(m => ({ ...m, type: 'monthly' })))
        let csv = 'Description,Invoice number,Type,Issued,Payment date,Payment method,Status,Total,Discount,Paid,Due\n'
        rows.forEach(item => {
          const oneOff = item.type === 'one_off'
          const total = oneOff ? parseFloat(item.amount_due) || 0 : parseFloat(item.actual_total) || 0
          const discount = oneOff ? 0 : parseFloat(item.discount) || 0
          const paid = oneOff ? parseFloat(item.amount_paid) || 0 : parseFloat(item.paid) || 0
          const due = oneOff ? total - paid : parseFloat(item.due) || 0
          const paidAt = oneOff ? item.charged_at : item.time_paid
          const method = item.payment_last_four && this.invoiceStatusText(item) === 'Paid' ? 'Card ending in ' + item.payment_last_four : ''
          csv += [
            this.escapeCSVValue(oneOff ? (item.description || '') : this.getMonthAndYear(item.billing_cycle_start)),
            item.invoice_id || '',
            oneOff ? 'One-off' : 'Statement',
            this.getInvoiceDate(oneOff ? item.created_at : item.billing_cycle_start),
            paidAt ? this.getLocalDate(paidAt) : '',
            this.escapeCSVValue(method),
            this.invoiceStatusText(item),
            (total - discount).toFixed(2),
            discount.toFixed(2),
            paid.toFixed(2),
            due.toFixed(2)
          ].join(',') + '\n'
        })

        const anchor = document.createElement('a')
        anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
        anchor.target = '_blank'
        anchor.download = 'invoices.csv'
        anchor.click()
      } catch (err) {
        console.log(err)
        this.$notify({ type: 'danger', message: 'Could not download invoices, please try again', icon: 'tim-icons icon-alert-circle-exc' })
      }
      this.loading = false
    },
    getOffset() {
      return Number((this.currentPage * this.perPage) - this.perPage)
    },
    openPayDrawer(item) {
      this.invoice_to_pay = item
      this.pay_drawer_open = true
    },
    onInvoicePaid() {
      this.pay_drawer_open = false
      this.getInvoices(this.perPage, this.getOffset())
    },
    switchTab(index) {
      this.activeTabIndex = index
      if (this.activeTabIndex === 1) {
        this.loadSquare()
      }
    },
    async loadSquare() {
      const script = document.createElement('script');
      script.src = 'https://web.squarecdn.com/v1/square.js'
      script.async = true;
      document.body.appendChild(script);
    },
    async initializeSquare() {
      if (!window.Square) {
        throw new Error('Square.js failed to load properly');
      }
      if (this.card) {
        await this.card.destroy()
      }

      this.square = window.Square.payments(this.squareAppId, this.squareLocationId);
      try {
        this.card = await this.initializeCard(this.square);
      } catch (e) {
        console.error('Initializing Card failed', e);
        return;
      }
    },
    async initializeCard(payments) {
      const customStyle = {
        '.input-container.is-focus': {
          borderColor: '#e14eca',
        },
        '.input-container.is-error': {
          borderColor: '#ff1600',
        },
        '.message-text.is-error': {
          color: '#ff1600',
        },
        '.message-icon.is-error': {
          color: '#ff1600',
        },
        'input.is-error': {
          color: '#ff1600',
        },
        '.message-icon': {
          color: '#adb5bd',
        },
        '@media screen and (max-width: 600px)': {
          'input': {
            'fontSize': '12px',
          }
        },
        '.input-container': {
          borderRadius: '6px',
        },
        '.message-text': {
          color: '#adb5bd',
        },
        input: {
          backgroundColor: localStorage.getItem('mode') === '0' ? '#ffffff' : '#22233a',
          color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff',
        },
        'input::placeholder': {
          color: '#adb5bd',
        },
      }
      const card = await payments.card({
        style: customStyle,
      });
      await card.attach('#card-container');
      return card;
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIsMobile)
  },
  mounted() {
    this.updateIsMobile()
    window.addEventListener('resize', this.updateIsMobile)
    if(localStorage.getItem('trial')){
      let trial = JSON.parse(localStorage.getItem('trial'))
      this.trial_ended = false
      this.trial_start = trial.trial_start
      this.trial_end = trial.trial_end
      let today = new Date();
      let trial_end = new Date(trial.trial_end);
      if(today > trial_end){
        this.trial_ended = true
      }
    }

    this.currency_symbol =  _.find(this.currencies, { code: JSON.parse(localStorage.getItem('group')).currency }).symbol
    this.getBillingInfo()
    if (this.$route.query.tab !== undefined) {
      this.switchTab(Number(this.$route.query.tab));
    } else if (this.$route.query.view !== undefined) {
      // A saved invoices view opened from the saved-views drawer lands on the invoices tab
      this.switchTab(2);
    }
  },
  watch: {
    '$route.query.tab'(newTab) {
      if (newTab !== undefined) {
        this.switchTab(Number(newTab));
      }
    },
    '$route.query.view'(view) {
      if (view !== undefined && this.$route.query.tab === undefined) {
        this.switchTab(2);
      }
    }
  }
}
</script>

<style scoped>
/* Canonical label/value row, copied by exact class name (same as PayInvoiceDrawer). */
.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
}
.detail-row:last-child {
  margin-bottom: 0;
}
.detail-row--total {
  font-size: 15px;
  font-weight: 600;
}
.white-content .detail-row {
  color: #525f7f;
}
</style>
